import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Heart, Users, Star } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';

export const BloodGroupsPage: React.FC = () => {
  const BASE_URL = import.meta.env.BASE_URL;
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqData = [
    {
      question: "What is the difference between blood types?",
      answer: "Blood types are determined by antigens on the surface of red blood cells. There are over 600 known antigens, but the most important are the ABO and Rh systems which determine the eight common blood types."
    },
    {
      question: "Can I donate blood to anyone?",
      answer: "Blood donation compatibility depends on your blood type and the recipient's blood type. For example, O-negative blood can be given to anyone, making it the universal donor type."
    },
    {
      question: "How do I know my blood type?",
      answer: "You can learn your blood type through blood donation (we'll tell you), a blood test ordered by your doctor, or some blood type testing kits. Many people also find out during medical procedures."
    },
    {
      question: "Is O-negative blood really the universal donor?",
      answer: "Yes, O-negative red blood cells can be given to any patient in emergency situations because they don't have A, B, or Rh antigens. However, O-negative blood is still tested for safety and compatibility."
    },
    {
      question: "Can I receive blood from any blood type?",
      answer: "No, you can only safely receive blood that's compatible with your blood type. AB-positive individuals are universal recipients for red blood cells, while O-negative individuals can receive only O-negative blood."
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Page Hero */}
      <section className="py-16 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-title font-bold text-neutral-900 mb-4">Understanding Blood Groups</h1>
          <p className="text-body-lg text-neutral-600 max-w-3xl mx-auto">
            Learn about blood types, compatibility rules, and why your blood type matters for blood donation and transfusion.
          </p>
        </div>
      </section>

      {/* Blood Type Basics */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Blood Type Basics</h2>
            <p className="text-body-lg text-neutral-600">
              The ABO and Rh systems determine the eight common blood types
            </p>
          </div>
          <Card>
            <CardContent>
              <div className="prose prose-lg max-w-none">
                <p className="text-body mb-6 text-neutral-700">
                  Every person's blood type is determined by antigens on the surface of red blood cells. 
                  These antigens can trigger immune responses if incompatible blood is transfused. The most 
                  important systems for blood transfusion are the ABO system and the Rh factor.
                </p>
                <p className="text-body mb-6 text-neutral-700">
                  The ABO system classifies blood based on A and B antigens:
                </p>
                <ul className="text-body mb-6 text-neutral-700 list-disc pl-6">
                  <li><strong>Type A:</strong> Has A antigens on red cells, anti-B antibodies in plasma</li>
                  <li><strong>Type B:</strong> Has B antigens on red cells, anti-A antibodies in plasma</li>
                  <li><strong>Type AB:</strong> Has both A and B antigens on red cells, no anti-A or anti-B antibodies in plasma</li>
                  <li><strong>Type O:</strong> Has no A or B antigens on red cells, has both anti-A and anti-B antibodies in plasma</li>
                </ul>
                <p className="text-body mb-6 text-neutral-700">
                  The Rh factor determines whether blood is positive or negative:
                </p>
                <ul className="text-body mb-6 text-neutral-700 list-disc pl-6">
                  <li><strong>Rh-positive:</strong> Has the D antigen on red cells</li>
                  <li><strong>Rh-negative:</strong> Does not have the D antigen on red cells</li>
                </ul>
                <p className="text-body text-neutral-700">
                  Combining these systems gives us the eight common blood types: A+, A-, B+, B-, AB+, AB-, O+, and O-.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Compatibility Chart */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Blood Type Compatibility Chart</h2>
            <p className="text-body-lg text-neutral-600">
              Understanding which blood types can donate to which recipients
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="text-center">
              <CardContent>
                <h3 className="text-subtitle font-semibold text-neutral-900 mb-4">Red Cell Compatibility</h3>
                <img 
                  src={`${BASE_URL}images/compatibility_chart_1_0.jpg`} 
                  alt="Blood type compatibility chart for red cells" 
                  className="w-full h-auto rounded-lg shadow-md"
                />
                <p className="text-small text-neutral-600 mt-4">
                  This chart shows which blood types can donate red cells to which recipients
                </p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent>
                <h3 className="text-subtitle font-semibold text-neutral-900 mb-4">Plasma Compatibility</h3>
                <img 
                  src={`${BASE_URL}images/compatibility_chart_2_9.png`} 
                  alt="Blood type compatibility chart for plasma" 
                  className="w-full h-auto rounded-lg shadow-md"
                />
                <p className="text-small text-neutral-600 mt-4">
                  Plasma compatibility follows different rules than red cell compatibility
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Blood Type Distribution */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Blood Type Distribution</h2>
            <p className="text-body-lg text-neutral-600">
              How common are different blood types in the U.S. population?
            </p>
          </div>
          <Card>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary-600 mb-2">37%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">O+</div>
                  <div className="text-small text-neutral-600">Most common</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-neutral-600 mb-2">36%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">A+</div>
                  <div className="text-small text-neutral-600">Second most common</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-neutral-600 mb-2">8%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">B+</div>
                  <div className="text-small text-neutral-600">Less common</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-neutral-600 mb-2">3%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">AB+</div>
                  <div className="text-small text-neutral-600">Rarest common type</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600 mb-2">7%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">O-</div>
                  <div className="text-small text-neutral-600">Universal donor</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-neutral-600 mb-2">6%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">A-</div>
                  <div className="text-small text-neutral-600">Less common</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-neutral-600 mb-2">2%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">B-</div>
                  <div className="text-small text-neutral-600">Rare</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-neutral-600 mb-2">1%</div>
                  <div className="text-body font-semibold text-neutral-900 mb-1">AB-</div>
                  <div className="text-small text-neutral-600">Rarest overall</div>
                </div>
              </div>
              <div className="mt-8 p-4 bg-primary-50 rounded-lg">
                <p className="text-body text-neutral-700 text-center">
                  <strong>Important:</strong> Only O+ and O- prevalence are specified in available sources. 
                  Specific prevalence for other types should be confirmed with local data or transfusion services.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Your Type Matters */}
      <section className="py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Why Your Type Matters</h2>
            <p className="text-body-lg text-neutral-600">
              Special considerations for different blood types
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card hover className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Star className="h-8 w-8 text-red-600" />
                </div>
                <CardTitle>O-Negative (Universal Donor)</CardTitle>
                <p className="text-body text-neutral-600 mt-4">
                  <strong>O-negative blood</strong> can be given to anyone in emergencies. About 7% of the population 
                  has this precious blood type. If you're O-negative, you're a hero waiting to happen!
                </p>
              </CardContent>
            </Card>
            <Card hover className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle>AB Plasma (Universal Plasma)</CardTitle>
                <p className="text-body text-neutral-600 mt-4">
                  <strong>AB plasma</strong> can be given to any blood type because it lacks anti-A and anti-B antibodies. 
                  Only about 4% of the population has AB blood, making AB plasma donors extremely valuable.
                </p>
              </CardContent>
            </Card>
            <Card hover className="text-center">
              <CardContent>
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle>Rare Blood Types</CardTitle>
                <p className="text-body text-neutral-600 mt-4">
                  <strong>Rare blood types</strong> are defined by the absence of antigens present in 99% or more of the population. 
                  These donors may face challenges finding matches and are highly encouraged to donate.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-subtitle font-bold text-neutral-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-body-lg text-neutral-600">
              Common questions about blood types and compatibility
            </p>
          </div>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <Card key={index}>
                <CardContent className="p-0">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full p-6 text-left flex items-center justify-between hover:bg-neutral-50 transition-colors"
                  >
                    <span className="text-body font-semibold text-neutral-900">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUp className="h-5 w-5 text-neutral-600" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-neutral-600" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-6">
                      <p className="text-body text-neutral-700">{faq.answer}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-subtitle font-bold mb-4">Ready to Donate?</h2>
          <p className="text-body-lg mb-8 text-red-100">
            Every blood type is valuable, and every donation saves lives. Find out if you're eligible to donate today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/donors" state={{ scrollTo: 'register-to-donate' }}>
              <Button size="lg" className="bg-white text-primary-600 hover:bg-gray-100">
                Register as Donor
              </Button>
            </Link>
            <Link to="/contact" state={{ scrollTo: 'contact-form' }}>
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-primary-600">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
